import { Stack } from '@mui/material';
import React from 'react';
import { useIntl } from 'react-intl';

import { ReferenceRangeChip } from '@components/Chip/';

import useReferenceRange from '@hooks/useReferenceRange';

import { capitalize } from '@utils/format';
import { displayFriendlyRiskLevel } from '@utils/reference_range';

import type {
  AlertParameterModel,
  ReferenceRangeRiskLevel,
} from 'generated/types';

interface AlertParameterListProps {
  code: string;
  parameters: AlertParameterModel[];
}

export default function AlertParameterList({
  code: name,
  parameters,
}: AlertParameterListProps) {
  const { formatMessage } = useIntl();
  const { displayFriendlyRange } = useReferenceRange();

  return (
    <Stack mt={1}>
      {parameters.map(
        ({
          alert_parameter_id,
          code,
          lower_bound,
          risk: _risk,
          upper_bound,
          is_custom,
        }) => {
          const risk = _risk as ReferenceRangeRiskLevel;
          const parameters = [
            {
              lower_bound,
              parameter_id: alert_parameter_id,
              upper_bound,
              code: code ?? '',
            },
          ];
          const riskMessageId = displayFriendlyRiskLevel(name, risk);
          const friendlyRisk = riskMessageId
            ? formatMessage({ id: riskMessageId })
            : '';

          /**
           * Custom label examples for BP & glucose
           * @shows Secondary code (e.g. systolic) -> Friendly risk -> lower & upper bounds -> Standard/Custom
           *
           * @ex Systolic High: Above or equal to 130-139 mmHg (Custom)
           * @ex Borderline: Above or equal to 7-Below 11 mmol/L (Standard)
           */
          const label = `${
            code ? capitalize(code) : ''
          } ${friendlyRisk}: ${displayFriendlyRange({
            code: name,
            parameters,
            is_custom,
            shorthand: true,
            include_clusivity: false,
          })}`;

          return (
            <ReferenceRangeChip
              code={name}
              key={`alert-parameter-${alert_parameter_id}`}
              label={label}
              parameters={parameters}
              risk={risk}
            />
          );
        }
      )}
    </Stack>
  );
}
